// Opt-in, local-only diagnostics. Never record bodies, news text, headers,
// full URLs, arbitrary error messages, or keypress values here.
export const DRAG_DIAGNOSTICS_VERSION = '2026-09-16.1';
export const DRAG_TRACE_KEY = 'pm.drag-debug.current.v1';
export const PREVIOUS_DRAG_TRACE_KEY = 'pm.drag-debug.previous.v1';
const MAX_EVENTS = 800;
const MAX_MILESTONES = 100;
type Fields = Record<string, string | number | boolean | null | undefined>;
type Entry = { seq: number; ms: number; event: string; data: Fields };
type Trace = {
  version: string;
  started: string;
  browser: Record<string, string | number>;
  checkpointAvailable: boolean;
  hasDrag: boolean;
  context: Fields;
  events: Entry[];
  milestones: Entry[];
};
export type DragDebugAPI = {
  snapshot: () => { current: Trace; previous?: Trace };
  download: () => void;
  copy: () => Promise<boolean>;
  stop: () => void;
};
declare global {
  interface Window {
    __PM_DRAG_DEBUG__?: DragDebugAPI;
  }
}

const numericKeys = new Set([
  'session_id',
  'player_id',
  'period',
  'session_status',
  'news',
  'news_count',
  'current_news_count',
  'instrument_count',
  'assignment_count',
  'pending_count',
  'capture_request_id',
  'request_id',
  'temp_id',
  'assignment',
  'value',
  'status',
  'duration_ms',
  'start_ms',
  'gap_ms',
  'frame_age_ms',
  'drag_id',
  'button',
  'buttons',
  'x',
  'y',
  'line',
  'column',
]);
const booleanKeys = new Set([
  'attached',
  'can_write',
  'persist_writes',
  'scope_matches',
  'drag_enabled',
  'current',
  'written',
  'trusted',
  'prevented',
  'draggable',
  'dimmed',
  'highlighted',
  'active_drag',
  'hidden',
  'focused',
  'custom_format',
  'text_format',
  'checkpoint_available',
]);
const tokenKeys = new Set([
  'action',
  'phase',
  'reason',
  'instrument_code',
  'pointer_type',
  'error_name',
  'tag',
  'effect_allowed',
  'drop_effect',
  'source',
]);
const safeFields = (fields: Fields): Fields => {
  const result: Fields = {};
  Object.entries(fields).forEach(([key, value]) => {
    if (
      numericKeys.has(key) &&
      typeof value === 'number' &&
      Number.isFinite(value)
    ) {
      result[key] = value;
    } else if (booleanKeys.has(key) && typeof value === 'boolean') {
      result[key] = value;
    } else if (
      tokenKeys.has(key) &&
      typeof value === 'string' &&
      /^[\w.=/:-]{1,80}$/.test(value)
    ) {
      result[key] = value;
    }
  });
  return result;
};
const safeError = (error: unknown): Fields => {
  try {
    return { error_name: error instanceof Error ? error.name : 'NonError' };
  } catch {
    return { error_name: 'UnknownError' };
  }
};
let recorder:
  | {
      record: (event: string, fields?: Fields, milestone?: boolean) => void;
      context: (fields: Fields) => void;
    }
  | undefined;

export const recordDragDiagnostic = (
  event: string,
  fields?: Fields,
  milestone = false,
) => {
  // Diagnostic failures must never interrupt a drag or an API operation.
  try {
    recorder?.record(event, fields, milestone);
  } catch {
    /* best effort */
  }
};
export const setDragDiagnosticContext = (fields: Fields) => {
  try {
    recorder?.context(fields);
  } catch {
    /* best effort */
  }
};
export const isDragDiagnosticsEnabled = () => Boolean(recorder);
export const recordDragDiagnosticError = (
  event: string,
  error: unknown,
  reason: string,
) => {
  if (recorder)
    recordDragDiagnostic(event, { ...safeError(error), reason }, true);
};
const assessmentActions = new Set([
  'FETCH_ASSESSMENT_STATE',
  'ASSIGN_ASSESSMENT_NEWS',
  'REVOKE_ASSESSMENT_ASSIGNMENT',
  'SET_ASSESSMENT_SLIDER',
]);
let requestId = 0;
export const traceAssessmentRequest = (action: string, payload: unknown) => {
  if (!recorder || !assessmentActions.has(action)) return undefined;
  try {
    // Generic StateManager payloads also contain auth data: explicit allowlist.
    const input = payload as Record<string, unknown>;
    const fields: Fields = { action, request_id: ++requestId };
    [
      'session_id',
      'capture_request_id',
      'temp_id',
      'news',
      'assignment',
      'value',
      'instrument_code',
    ].forEach(key => {
      const value = input?.[key];
      if (typeof value === 'number' || typeof value === 'string')
        fields[key] = value;
    });
    const started = performance.now();
    recordDragDiagnostic('request.start', fields, true);
    return {
      response: (status: unknown) =>
        recordDragDiagnostic(
          'request.response',
          {
            ...fields,
            status: typeof status === 'number' ? status : undefined,
            duration_ms: Math.round(performance.now() - started),
          },
          true,
        ),
      failed: (error: unknown) =>
        recordDragDiagnostic(
          'request.failed',
          {
            ...fields,
            ...safeError(error),
            duration_ms: Math.round(performance.now() - started),
          },
          true,
        ),
    };
  } catch {
    return undefined;
  }
};

export const startDragDiagnostics = (): DragDebugAPI | undefined => {
  if (typeof window === 'undefined') return undefined;
  if (window.__PM_DRAG_DEBUG__) return window.__PM_DRAG_DEBUG__;
  // No storage access, listeners, UI or timers for ordinary users.
  if (new URLSearchParams(window.location.search).get('dragDebug') !== '1')
    return undefined;
  const cleanup: (() => void)[] = [];
  let panel: HTMLElement | undefined;
  try {
    const started = performance.now();
    let running = true;
    let seq = 0;
    let dragId = 0;
    let activeDrag = false;
    let storage: Storage | undefined;
    try {
      storage = window.sessionStorage;
    } catch {
      /* browser storage policy */
    }
    const readStored = (key: string): Trace | undefined => {
      let raw: string | null | undefined;
      try {
        raw = storage?.getItem(key);
      } catch {
        storage = undefined;
        return undefined;
      }
      try {
        if (!raw || raw.length > 500000) return undefined;
        const value = JSON.parse(raw) as Trace;
        if (
          value.version !== DRAG_DIAGNOSTICS_VERSION ||
          !value.hasDrag ||
          !Array.isArray(value.events) ||
          value.events.length > MAX_EVENTS ||
          !Array.isArray(value.milestones) ||
          value.milestones.length > MAX_MILESTONES
        )
          return undefined;
        return value;
      } catch {
        return undefined;
      }
    };
    // Preserve the last drag trace across recovery reloads, without recursively
    // storing the previous trace inside the new one.
    const previous =
      readStored(DRAG_TRACE_KEY) || readStored(PREVIOUS_DRAG_TRACE_KEY);
    if (previous) {
      try {
        storage?.setItem(PREVIOUS_DRAG_TRACE_KEY, JSON.stringify(previous));
      } catch {
        storage = undefined;
      }
    }
    const trace: Trace = {
      version: DRAG_DIAGNOSTICS_VERSION,
      started: new Date().toISOString(),
      browser: {
        userAgent: navigator.userAgent.slice(0, 512),
        platform: navigator.platform,
        width: window.innerWidth,
        height: window.innerHeight,
        dpr: window.devicePixelRatio,
        touchPoints: navigator.maxTouchPoints || 0,
        cores: navigator.hardwareConcurrency || 0,
        bundle:
          Array.from(document.scripts)
            .map(
              script => script.src.match(/\/(main\.[\w.-]+\.js)(?:\?|$)/)?.[1],
            )
            .find(Boolean) || 'unknown',
      },
      checkpointAvailable: Boolean(storage),
      hasDrag: false,
      context: {},
      events: [],
      milestones: [],
    };
    let status: HTMLElement | undefined;
    const updateStatus = () => {
      if (status)
        status.textContent = `${running ? 'Recording' : 'Stopped'} · ${trace.checkpointAvailable ? 'reload recovery on' : 'memory only — preserve Console log'}${previous ? ' · previous trace recovered' : ''}`;
    };
    const record = (event: string, fields: Fields = {}, milestone = false) => {
      if (!running) return;
      const entry: Entry = {
        seq: ++seq,
        ms: Math.round(performance.now() - started),
        event,
        data: safeFields(fields),
      };
      trace.events.push(entry);
      if (trace.events.length > MAX_EVENTS) trace.events.shift();
      if (milestone) {
        trace.milestones.push(entry);
        if (trace.milestones.length > MAX_MILESTONES) trace.milestones.shift();
        // Strings stay readable after reload with DevTools Preserve log enabled.
        try {
          console.info(`[PM drag] ${JSON.stringify(entry)}`);
        } catch {
          /* best effort */
        }
      }
    };
    const checkpoint = () => {
      if (!storage) return;
      try {
        storage.setItem(DRAG_TRACE_KEY, JSON.stringify(trace));
      } catch {
        storage = undefined;
        trace.checkpointAvailable = false;
        record('checkpoint.unavailable', {}, true);
        updateStatus();
      }
    };
    const listen = (
      target: EventTarget,
      name: string,
      fn: (event: any) => void,
      capture = false,
    ) => {
      const guarded: EventListener = event => {
        try {
          fn(event);
        } catch {
          /* observation cannot affect input */
        }
      };
      target.addEventListener(name, guarded, { capture, passive: true });
      cleanup.push(() => target.removeEventListener(name, guarded, capture));
    };
    const targetFields = (target: EventTarget | null): Fields => {
      const element =
        target instanceof Element
          ? target
          : target instanceof Node
            ? target.parentElement
            : null;
      if (!element) return {};
      const source = element.closest<HTMLElement>('[data-news-id]');
      const instrument = element.closest<HTMLElement>('[data-assessment-code]');
      return {
        tag: element.tagName,
        news: source ? Number(source.dataset.newsId) : undefined,
        draggable: source?.draggable,
        dimmed: source?.classList.contains('is-dragging'),
        instrument_code: instrument?.dataset.assessmentCode,
        highlighted: instrument?.classList.contains('is-drop-target'),
      };
    };
    const sampled = new Map<string, number>();
    const observeInput = (
      event: DragEvent | PointerEvent | KeyboardEvent,
      phase: string,
    ) => {
      if (
        event.target instanceof Element &&
        event.target.closest('[data-pm-drag-debug]')
      )
        return;
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
      const fields = targetFields(event.target);
      if (event.type === 'pointerdown' && fields.news) trace.hasDrag = true;
      // Keep outside/overlay pointer events too: they distinguish a missed
      // source from a browser that has stopped delivering input altogether.
      const frequent = ['drag', 'dragover', 'dragenter', 'dragleave'].includes(
        event.type,
      );
      if (frequent) {
        // Fixed-size keys even if thousands of DOM targets are visited.
        const last = sampled.get(event.type);
        if (last !== undefined && performance.now() - last < 250) return;
        sampled.set(event.type, performance.now());
      }
      if (phase === 'capture' && event.type === 'dragstart') {
        dragId += 1;
        activeDrag = true;
        trace.hasDrag = true;
      }
      if (
        phase === 'capture' &&
        ['drop', 'dragend', 'pointerdown'].includes(event.type)
      )
        activeDrag = false;
      const transfer = (event as DragEvent).dataTransfer;
      const types = transfer ? Array.from(transfer.types || []) : [];
      const pointer = event as PointerEvent;
      record(
        `native.${event.type}`,
        {
          ...fields,
          phase,
          drag_id: dragId,
          active_drag: activeDrag,
          trusted: event.isTrusted,
          prevented: event.defaultPrevented,
          button: pointer.button,
          buttons: pointer.buttons,
          x: pointer.clientX,
          y: pointer.clientY,
          pointer_type: pointer.pointerType,
          effect_allowed: transfer?.effectAllowed,
          drop_effect: transfer?.dropEffect,
          custom_format: types.includes('application/x-pm-v4-assessment-news'),
          text_format: types.includes('text/plain'),
        },
        !frequent,
      );
      // Before React's drag handler and again after it; no storage writes on hover.
      if (
        ['dragstart', 'dragend', 'drop'].includes(event.type) ||
        (event.type === 'pointerdown' && fields.news)
      )
        checkpoint();
    };
    [
      'pointerdown',
      'pointerup',
      'pointercancel',
      'mousedown',
      'mouseup',
      'click',
      'gotpointercapture',
      'lostpointercapture',
      'keydown',
      'dragstart',
      'drag',
      'dragenter',
      'dragleave',
      'dragover',
      'drop',
      'dragend',
    ].forEach(name => {
      listen(document, name, event => observeInput(event, 'capture'), true);
    });
    ['dragstart', 'drop', 'dragend'].forEach(name =>
      listen(document, name, event => observeInput(event, 'bubble')),
    );
    ['focus', 'blur', 'pageshow', 'pagehide'].forEach(name =>
      listen(window, name, () => {
        record(
          `page.${name}`,
          { hidden: document.hidden, focused: document.hasFocus() },
          true,
        );
        checkpoint();
      }),
    );
    listen(document, 'visibilitychange', () => {
      record(
        'page.visibility',
        { hidden: document.hidden, focused: document.hasFocus() },
        true,
      );
      checkpoint();
    });
    listen(window, 'error', (event: ErrorEvent) => {
      record(
        'page.error',
        {
          ...safeError(event.error),
          line: event.lineno,
          column: event.colno,
          source: event.filename?.split('/').pop()?.split('?')[0],
        },
        true,
      );
      // Source positions only; error messages / stack first lines may contain data.
      if (event.error instanceof Error) {
        const frames =
          event.error.stack
            ?.split('\n')
            .slice(1)
            .join('\n')
            .match(/[\w.-]+\.(?:js|tsx?):\d+:\d+/g)
            ?.slice(0, 8) || [];
        frames.forEach(source => record('page.error.frame', { source }, true));
      }
      checkpoint();
    });
    listen(window, 'unhandledrejection', (event: PromiseRejectionEvent) => {
      record('page.rejection', safeError(event.reason), true);
      checkpoint();
    });
    try {
      if (
        typeof PerformanceObserver !== 'undefined' &&
        PerformanceObserver.supportedEntryTypes?.includes('longtask')
      ) {
        const observer = new PerformanceObserver(list => {
          list.getEntries().forEach(entry =>
            record('main.longtask', {
              duration_ms: Math.round(entry.duration),
              start_ms: Math.round(entry.startTime - started),
              active_drag: activeDrag,
            }),
          );
        });
        observer.observe({ entryTypes: ['longtask'] });
        cleanup.push(() => observer.disconnect());
      }
    } catch {
      /* not supported in every browser */
    }
    let lastBeat = performance.now();
    let lastFrame = lastBeat;
    let frame: number | undefined;
    const heartbeat = window.setInterval(() => {
      try {
        const now = performance.now();
        record('main.heartbeat', {
          gap_ms: Math.round(now - lastBeat),
          frame_age_ms: Math.round(now - lastFrame),
          hidden: document.hidden,
          focused: document.hasFocus(),
          active_drag: activeDrag,
          drag_id: dragId,
        });
        lastBeat = now;
        if (frame === undefined)
          frame = window.requestAnimationFrame(() => {
            frame = undefined;
            lastFrame = performance.now();
          });
        checkpoint();
      } catch {
        /* best effort */
      }
    }, 1000);
    cleanup.push(() => {
      window.clearInterval(heartbeat);
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    });
    const api: DragDebugAPI = {
      snapshot: () => JSON.parse(JSON.stringify({ current: trace, previous })),
      download: () => {
        const url = URL.createObjectURL(
          new Blob([JSON.stringify(api.snapshot(), null, 2)], {
            type: 'application/json',
          }),
        );
        const link = document.createElement('a');
        link.href = url;
        link.download = `pm-drag-trace-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      copy: async () => {
        try {
          await navigator.clipboard.writeText(
            JSON.stringify(api.snapshot(), null, 2),
          );
          return true;
        } catch {
          return false;
        }
      },
      stop: () => {
        if (!running) return;
        record('diagnostics.stopped', {}, true);
        checkpoint();
        running = false;
        recorder = undefined;
        cleanup.forEach(fn => fn());
        updateStatus();
      },
    };
    // Outside React so component errors do not remove the recovery controls.
    panel = document.createElement('aside');
    panel.dataset.pmDragDebug = 'true';
    panel.setAttribute('aria-label', 'Drag diagnostics');
    panel.style.cssText =
      'position:fixed;right:8px;bottom:8px;z-index:2147483647;max-width:300px;padding:10px;background:#18202c;color:#fff;border:1px solid #7b95b4;border-radius:6px;font:12px/1.5 system-ui;box-shadow:0 2px 8px #0005;';
    const title = document.createElement('strong');
    title.textContent = `Drag diagnostics ${DRAG_DIAGNOSTICS_VERSION}`;
    status = document.createElement('div');
    panel.append(title, status);
    const addButton = (label: string, action: () => void | Promise<void>) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.style.cssText =
        'margin:6px 6px 0 0;padding:4px 8px;background:#fff;color:#18202c;border:0;border-radius:3px;cursor:pointer;font:inherit;';
      button.onclick = () => {
        void action();
      };
      panel!.appendChild(button);
    };
    addButton('Download trace', () => {
      try {
        api.download();
      } catch {
        status!.textContent = 'Download failed — try Copy trace or DevTools.';
      }
    });
    addButton('Copy trace', async () => {
      status!.textContent = (await api.copy())
        ? 'Trace copied.'
        : 'Clipboard unavailable — use Download trace.';
    });
    addButton('Stop recording', api.stop);
    document.body.appendChild(panel);
    recorder = {
      record,
      context: fields => {
        const next = { ...trace.context, ...safeFields(fields) };
        if (JSON.stringify(next) === JSON.stringify(trace.context)) return;
        trace.context = next;
        record('app.context', next, true);
      },
    };
    window.__PM_DRAG_DEBUG__ = api;
    record(
      'diagnostics.started',
      { checkpoint_available: trace.checkpointAvailable },
      true,
    );
    checkpoint();
    updateStatus();
    return api;
  } catch {
    recorder = undefined;
    cleanup.forEach(fn => {
      try {
        fn();
      } catch {
        /* best effort */
      }
    });
    panel?.remove();
    return undefined;
  }
};
